import React, { useMemo, useRef, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import { ColumnDirective, ColumnsDirective, Filter, GridComponent, Group } from '@syncfusion/ej2-react-grids';
import { Inject, Page, Sort, Search, Toolbar, Edit } from '@syncfusion/ej2-react-grids';
import { DropDownList } from '@syncfusion/ej2-react-dropdowns';

const ViewSamples = ({ samples, dropDownOptions, sampleName, handleAction }) => {
  const gridRef = useRef(null);

  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [selectedTakeOut, setSelectedTakeOut] = useState('');

  const filledSamples = useMemo(() => {
    const defaultRow = {
      id: null, // no id for new rows
      sampleName: sampleName,
      species: '',
      dbh: '',
      takeOut: '',
      isDummy: true,
    };

    const padded = [...samples.map(s => ({ ...s, isDummy: false }))];

    console.log('padded ===>', padded, samples);

    for (let i = padded.length; i < 24; i++) {
      padded.push({ ...defaultRow, isDummy: true });
    }

    return padded.map((row, idx) => ({ ...row, recordNumber: idx + 1 }));
  }, [samples]);

  const speciesDropdown = dropDownOptions?.species?.map(s => ({ text: s, value: s }));

  let speciesDropDownInstance;
  let takeOutDropdownInstance;

  const speciesEditor = {
    create: () => {
      const input = document.createElement('input');
      input.className = 'e-input';
      return input;
    },
    write: args => {
      setSelectedSpecies(args.rowData?.species ?? '');
      speciesDropDownInstance = new DropDownList({
        dataSource: speciesDropdown,
        fields: { text: 'text', value: 'value' },
        value: args.rowData?.species ?? '',
        allowFiltering: true,
        placeholder: 'Select species',
        change: e => {
          console.log(e);
          setSelectedSpecies(e.value);
        },
      });
      speciesDropDownInstance.appendTo(args.element);
    },
    read: () => {
      return selectedSpecies;
    },
    destroy: () => {
      if (speciesDropDownInstance) {
        speciesDropDownInstance.destroy();
      }
    },
  };

  const takeOutEditor = {
    create: () => document.createElement('input'),
    read: () => takeOutDropdownInstance?.value ?? '',
    write: args => {
      setSelectedTakeOut(args.rowData?.takeOut ?? '');
      takeOutDropdownInstance = new DropDownList({
        dataSource: ['Yes', 'No'].map(s => ({ text: s, value: s })),
        fields: { text: 'text', value: 'value' },
        value: args.rowData?.takeOut ?? '',
        allowFiltering: true,
        placeholder: 'Select',
        change: e => {
          console.log(e);
          setSelectedTakeOut(e.value);
        },
      });
      takeOutDropdownInstance.appendTo(args.element);
    },
    read: () => {
      return selectedTakeOut;
    },
    destroy: () => {
      if (speciesDropDownInstance) {
        speciesDropDownInstance.destroy();
      }
    },
  };

  const pageSettings = useMemo(
    () => ({
      pageSize: 30,
      pageSizes: [5, 10, 20, 30, 40],
    }),
    [],
  );

  const filterSettings = {
    type: 'Excel',
  };

  const editSettings = {
    allowEditing: true,
    allowAdding: true,
    allowDeleting: true,
    mode: 'Inline',
    showConfirmDialog: true,
    showDeleteConfirmDialog: true,
  };

  const toolbarOptions = ['Edit', 'Update', 'Cancel'];

  const actionBegin = args => {
    if (args.requestType === 'save') {
      const { species, dbh, takeOut } = args.data;

      // Skip empty rows
      const isEmpty = !species && !dbh && !takeOut;
      if (isEmpty) {
        args.cancel = true;
        return;
      }

      // Strip dummy flag
      delete args.data.isDummy;
      delete args.data.recordNumber;

      // If new (no ID), remove id field
      if (!args.data.id) {
        delete args.data.id;
      }
    }
  };

  const actionComplete = args => {
    if (args.requestType === 'save') {
      const isUpdate = !!args.data.id;

      // Call parent handler but DON'T wait for it
      setTimeout(() => {
        handleAction(args.data, isUpdate ? 'update' : 'add');
      }, 100); // Delay lets grid finish first
    }
  };

  const actionFailure = args => {
    console.error('Action Failed:', args);
  };

  if (!filledSamples) {
    return <CircularProgress />;
  }

  return (
    <div>
      <Alert severity="info">To edit any row. double tap inside the cell</Alert>
      <GridComponent
        ref={gridRef}
        dataSource={filledSamples}
        filterSettings={filterSettings}
        allowPaging={true}
        allowSorting={true}
        allowFiltering={true}
        allowTextWrap={true}
        allowResizing={true}
        allowMultiSorting={true}
        allowReordering={true}
        selectionSettings={{ type: 'Single', mode: 'Row' }}
        pageSettings={pageSettings}
        toolbar={toolbarOptions}
        editSettings={editSettings}
        actionBegin={actionBegin}
        actionComplete={actionComplete}
        actionFailure={actionFailure}
        height="400px">
        <ColumnsDirective>
          {/* <ColumnDirective field="recordNumber" headerText="Record" width="80" textAlign="Center" allowEditing={false} /> */}
          <ColumnDirective
            field="id"
            headerText="Record"
            width="80"
            isPrimaryKey={true}
            textAlign="Left"
            allowEditing={false}
            template={rowData => `${rowData.recordNumber}`}
          />
          <ColumnDirective field="species" headerText="Species" width="100" editType="dropdownedit" edit={speciesEditor} />
          <ColumnDirective
            field="dbh"
            headerText="DBH"
            width="100"
            textAlign="Right"
            editType="numericedit"
            edit={{ params: { min: 0, max: 1000, step: 0.1 } }}
          />
          <ColumnDirective field="takeOut" headerText="Take Out" width="100" editType="dropdownedit" edit={takeOutEditor} />
        </ColumnsDirective>
        <Inject services={[Page, Sort, Filter, Group, Search, Toolbar, Edit]} />
      </GridComponent>
    </div>
  );
};

export default ViewSamples;
